import Checkmark16 from '@carbon/icons-react/es/checkmark/16';
import Close16 from '@carbon/icons-react/es/close/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Unlink16 from '@carbon/icons-react/es/unlink/16';
import { addLinkProtocolIfApplicable } from '@ckeditor/ckeditor5-link/src/utils';
import { Input } from 'antd';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'components/atoms/button';
import { useTranslation } from 'hooks/use-translation';
import {
  PopupType,
  setBalloonToolbarDetails,
  useDocuments,
} from '../../document-store';
import styles from './add-link-form.module.css';

interface LinkFormProps {
  edit: boolean;
  href: string;
}
export const LinkForm: FC<LinkFormProps> = ({ edit, href = '' }) => {
  const [state, dispatch] = useDocuments();
  const [link, setLink] = useState(href);
  const inputRef = useRef<Input>(null);
  const editInputRef = useRef<Input>(null);
  const [readOnly, setReadonly] = useState(true);
  const toggleEditLink = () => {
    setLink(href);
    setReadonly(!readOnly);
  };

  const closeForm = useCallback(() => {
    setBalloonToolbarDetails(dispatch, {
      type: PopupType.BalloonToolbar,
      position: {
        x: 0,
        y: 0,
      },
    });
  }, [dispatch]);
  const addLink = (e) => {
    e.preventDefault();
    state.editor?.execute(
      'link',
      addLinkProtocolIfApplicable(
        link,
        state.editor?.config.get('link.defaultProtocol'),
      ),
    );
    setLink('');
    closeForm();
    toggleEditLink();
    setReadonly(true);
  };
  const escFunction = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        closeForm();
      }
    },
    [closeForm],
  );
  const unlink = () => {
    setLink('');
    toggleEditLink();
    state.editor?.execute('unlink');
    closeForm();
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    setLink(href);
  }, [href]);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const linkPlaceholder = useTranslation('link.placeholder');

  return edit ? (
    <div className={styles.form}>
      {readOnly ? (
        <a className={styles.link} href={href}>
          {href}
        </a>
      ) : (
        <Input
          ref={editInputRef}
          value={link}
          placeholder={linkPlaceholder}
          className={styles.input}
          onPressEnter={(e) => e.currentTarget.blur()}
          onChange={(e) => setLink(e.target.value)}
          onBlur={addLink}
        />
      )}
      <Button
        size="small"
        type="text"
        htmlType="submit"
        onClick={toggleEditLink}
        icon={
          readOnly ? (
            <Edit16 />
          ) : (
            <Checkmark16 color="var(--ck-color-button-save)" />
          )
        }
      />
      <Button
        size="small"
        type="text"
        onClick={unlink}
        icon={<Unlink16 color="var(--ck-color-button-cancel)" />}
      />
    </div>
  ) : (
    <form className={styles.form} onSubmit={addLink}>
      <Input
        value={link}
        ref={inputRef}
        placeholder="https://example.com"
        size="large"
        className={styles.input}
        onPressEnter={(e) => {
          e.currentTarget.blur();
          addLink(e);
        }}
        onChange={(e) => setLink(e.target.value)}
      />
      <Button
        size="small"
        type="text"
        htmlType="submit"
        onClick={addLink}
        icon={<Checkmark16 color="var(--ck-color-button-save)" />}
      />
      <Button
        size="small"
        type="text"
        onClick={closeForm}
        icon={<Close16 color="var(--ck-color-button-cancel)" />}
      />
    </form>
  );
};
